/* D51 -- Foreground field-trip GPS pinger.
 *
 * Contract:
 *   * `startPinging(tripId)` -- begins a 5-minute watchPosition loop
 *     (LOW accuracy, cellular / WiFi positioning; low battery drain).
 *     Denoise: skip a ping if the last one is < 200 m away AND < 10 min
 *     old. Store every non-skipped ping in the local IndexedDB queue.
 *   * `stopPinging()` -- stops the loop.
 *   * `pingEvent(source)` -- one-shot HIGH accuracy ping. Used at
 *     `punch_in`, `punch_out`, `customer_visit` events. source is
 *     one of the enum values in the backend PingIn.
 *   * `flushQueue()` -- drains the IndexedDB queue via a single
 *     `POST /api/field-trips/{trip_id}/ping` batch. Called on
 *     `online` event + on `stopPinging()`.
 *   * `isPinging()` / `currentTripId()` -- inspectors for the banner.
 *
 * Foreground-only for D51 -- when the tab is backgrounded / mobile
 * app goes to sleep, watchPosition often stops firing. We accept this
 * limitation (user explicitly OK'd) and hoist background tracking to
 * a future native-wrapper dispatch. Client shows the "Trip active"
 * banner so the employee knows they must keep the tab open.
 *
 * IndexedDB schema: single object store `pings` keyed by autoincrement
 * with fields matching the backend PingIn contract + a `trip_id` and
 * `queued_at` client timestamp.
 */
import api from "@/lib/api";

const DB_NAME = "sharda_field_pings_v1";
const DB_STORE = "pings";
const CADENCE_MS = 5 * 60 * 1000;           // 5 minutes
const DENOISE_RADIUS_M = 200;
const DENOISE_STALE_MIN_MS = 10 * 60 * 1000;
const BATCH_MAX = 50;

let _watchId = null;
let _cadenceTimer = null;
let _currentTripId = null;
let _lastAcceptedPing = null;    // {lat, lng, at (ms)}

// ---------- IndexedDB helpers ----------

function _openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not supported"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DB_STORE)) {
        db.createObjectStore(DB_STORE, { keyPath: "_k", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function _enqueue(ping) {
  try {
    const db = await _openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(DB_STORE, "readwrite");
      tx.objectStore(DB_STORE).add({ ...ping, queued_at: Date.now() });
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  } catch (_e) { return false; }
}

async function _drain(tripId) {
  const db = await _openDb();
  return new Promise((resolve) => {
    const out = [];
    const keys = [];
    const tx = db.transaction(DB_STORE, "readonly");
    const store = tx.objectStore(DB_STORE);
    const cur = store.openCursor();
    cur.onsuccess = (e) => {
      const c = e.target.result;
      if (!c) { resolve({ pings: out, keys }); return; }
      const v = c.value || {};
      if (v.trip_id === tripId) {
        const { _k, queued_at, trip_id, ...rest } = v;
        // Rename _k so it stays with the delete step but is stripped
        // from the outgoing payload.
        keys.push(c.primaryKey);
        out.push(rest);
      }
      c.continue();
    };
  });
}

async function _deleteKeys(keys) {
  if (!keys.length) return;
  const db = await _openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(DB_STORE, "readwrite");
    const store = tx.objectStore(DB_STORE);
    keys.forEach((k) => store.delete(k));
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => resolve(false);
  });
}

// ---------- Geolocation helpers ----------

function _getPositionOnce(opts) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({
        lat: p.coords.latitude, lng: p.coords.longitude,
        accuracy_m: p.coords.accuracy || null,
        captured_at: new Date(p.timestamp || Date.now()).toISOString(),
      }),
      () => resolve(null),
      opts,
    );
  });
}

function _haversineM(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => v == null)) return 0;
  const R = 6371008.8;
  const p1 = lat1 * Math.PI / 180;
  const p2 = lat2 * Math.PI / 180;
  const dp = (lat2 - lat1) * Math.PI / 180;
  const dl = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dp / 2) ** 2
        + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function _shouldDenoise(pos) {
  if (!_lastAcceptedPing) return false;
  const dist = _haversineM(_lastAcceptedPing.lat, _lastAcceptedPing.lng,
                              pos.lat, pos.lng);
  const age = Date.now() - _lastAcceptedPing.at;
  return dist < DENOISE_RADIUS_M && age < DENOISE_STALE_MIN_MS;
}

// ---------- Public API ----------

export async function startPinging(tripId) {
  if (_currentTripId === tripId) return;
  await stopPinging();
  _currentTripId = tripId;
  _lastAcceptedPing = null;
  // Immediate first ping (HIGH accuracy) at trip start.
  await pingEvent("punch_out").catch(() => null);
  // Periodic loop.
  _cadenceTimer = setInterval(() => {
    _cadenceTick().catch(() => null);
  }, CADENCE_MS);
  // Fire an initial tick after a short delay so we get a data point
  // even if the user closes the tab soon.
  setTimeout(() => { _cadenceTick().catch(() => null); }, 45 * 1000);
  // Auto-flush on reconnect.
  try {
    window.addEventListener("online", _onOnline);
  } catch (_e) { /* SSR / no-window */ }
}

export async function stopPinging() {
  if (_cadenceTimer) { clearInterval(_cadenceTimer); _cadenceTimer = null; }
  if (_watchId != null && navigator.geolocation) {
    try { navigator.geolocation.clearWatch(_watchId); } catch (_e) { /* ignore */ }
    _watchId = null;
  }
  const tripId = _currentTripId;
  if (tripId) {
    // Flush any remaining queued pings before we forget the trip id.
    await flushQueue(tripId).catch(() => null);
  }
  _currentTripId = null;
  _lastAcceptedPing = null;
  try { window.removeEventListener("online", _onOnline); }
  catch (_e) { /* ignore */ }
}

export async function pingEvent(source /* punch_in|punch_out|customer_visit|manual */) {
  const tripId = _currentTripId;
  if (!tripId) return { skipped: "no-active-trip" };
  const pos = await _getPositionOnce({
    enableHighAccuracy: true, timeout: 15000, maximumAge: 0,
  });
  if (!pos) return { skipped: "geo-unavailable" };
  const ping = {
    ...pos,
    source,
    hi_accuracy: true,
    is_offline_backfilled: false,
    battery_pct: await _readBattery(),
  };
  _lastAcceptedPing = { lat: pos.lat, lng: pos.lng, at: Date.now() };
  await _enqueue({ trip_id: tripId, ...ping });
  await flushQueue(tripId).catch(() => null);
  return { ok: true };
}

async function _cadenceTick() {
  const tripId = _currentTripId;
  if (!tripId) return;
  const pos = await _getPositionOnce({
    enableHighAccuracy: false, timeout: 20000, maximumAge: 30000,
  });
  if (!pos) return;
  if (_shouldDenoise(pos)) return;    // parked at customer denoise
  const ping = {
    ...pos, source: "auto_5min", hi_accuracy: false,
    is_offline_backfilled: false, battery_pct: await _readBattery(),
  };
  _lastAcceptedPing = { lat: pos.lat, lng: pos.lng, at: Date.now() };
  await _enqueue({ trip_id: tripId, ...ping });
  await flushQueue(tripId).catch(() => null);
}

async function _readBattery() {
  try {
    if (typeof navigator !== "undefined" && navigator.getBattery) {
      const b = await navigator.getBattery();
      return Math.round((b.level || 0) * 100);
    }
  } catch (_e) { /* ignore */ }
  return null;
}

export async function flushQueue(tripId) {
  if (!tripId) tripId = _currentTripId;
  if (!tripId) return { flushed: 0 };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { flushed: 0, offline: true };
  }
  const { pings, keys } = await _drain(tripId);
  if (!pings.length) return { flushed: 0 };
  // POST in batches of BATCH_MAX so a single failure doesn't lose all.
  let ok = 0;
  for (let i = 0; i < pings.length; i += BATCH_MAX) {
    const chunk = pings.slice(i, i + BATCH_MAX);
    const chunkKeys = keys.slice(i, i + BATCH_MAX);
    try {
      // Any ping being flushed AFTER an offline gap gets flagged so
      // the backend + downstream analytics can distinguish live vs
      // recovered pings. If the client submitted immediately when
      // online the flag was already false.
      const payload = { pings: chunk.map((p) => ({
          ...p,
          is_offline_backfilled: p.is_offline_backfilled
                                   || (navigator.onLine === false),
        })) };
      await api.post(`/field-trips/${tripId}/ping`, payload,
                        { silent: true });
      await _deleteKeys(chunkKeys);
      ok += chunk.length;
    } catch (_e) {
      // Retry on next flushQueue call (online event / next tick).
      break;
    }
  }
  return { flushed: ok, remaining: pings.length - ok };
}

function _onOnline() {
  const tripId = _currentTripId;
  if (tripId) flushQueue(tripId).catch(() => null);
}

export function isPinging() { return _currentTripId != null; }
export function currentTripId() { return _currentTripId; }
